import {
  createContext,
  useContext,
  useEffect,
  useState,
  type FormEvent,
  type ReactNode
} from "react";
import { api, ApiError } from "../lib/api.js";
import type { SessionUser } from "../lib/types.js";
import { BrandMark } from "../components/BrandMark.js";
import { Icon } from "../components/Icon.js";

interface AuthContextValue {
  user: SessionUser;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthGate");
  return ctx;
}

/**
 * Gates the whole application behind a valid session. There is no public
 * registration; a single administrator account is provisioned server-side.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState<SessionUser | null>(null);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .session()
      .then((res) => {
        if (cancelled) return;
        if (res.authenticated && res.user) setUser(res.user);
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await api.login(username.trim(), password);
      if (res.success && res.user) {
        setUser(res.user);
        setPassword("");
      } else {
        setError(res.message ?? "Invalid username or password.");
      }
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? "Too many attempts. Please wait a minute and try again."
          : err instanceof Error
            ? err.message
            : "Sign in failed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const logout = async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
  };

  if (checking) {
    return (
      <div className="auth-screen">
        <div className="col gap-3" style={{ alignItems: "center", color: "var(--text-muted)" }}>
          <Icon name="book" size={26} className="spin" />
          <span>Loading ClovaScribe…</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-screen">
        <div className="auth-card card">
          <div style={{ marginBottom: 26 }}>
            <BrandMark size={46} subtitle="Your private writing workspace" />
          </div>
          <form onSubmit={onSubmit}>
            <div className="field">
              <label className="label" htmlFor="username">Username</label>
              <input
                id="username"
                className="input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="password">Password</label>
              <input
                id="password"
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            {error && (
              <div className="auth-error row gap-2">
                <Icon name="alert" size={15} />
                <span>{error}</span>
              </div>
            )}
            <button className="btn btn-primary" type="submit" disabled={submitting} style={{ width: "100%", marginTop: 6 }}>
              {submitting ? <Icon name="clock" size={15} className="spin" /> : <Icon name="logout" size={15} />}
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <p className="faint" style={{ fontSize: 11.5, textAlign: "center", marginTop: 22, marginBottom: 0 }}>
            Private application · access by invitation only
          </p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={{ user, logout }}>{children}</AuthContext.Provider>;
}
